import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { StudentPickerMulti } from "@/components/StudentPicker";
import { toast } from "sonner";
import { AlertTriangle, Save, RotateCcw, History, GraduationCap, Users, Search, X } from "lucide-react";

const ACADEMIC_YEARS = ["2024/2025", "2025/2026", "2026/2027"];
const GRADE_OPTIONS = [
  { value: "-", label: "—" },
  { value: "18", label: "18" },
  { value: "19", label: "19" },
  { value: "20", label: "20" },
  { value: "21", label: "21" },
  { value: "22", label: "22" },
  { value: "23", label: "23" },
  { value: "24", label: "24" },
  { value: "25", label: "25" },
  { value: "26", label: "26" },
  { value: "27", label: "27" },
  { value: "28", label: "28" },
  { value: "29", label: "29" },
  { value: "30", label: "30" },
  { value: "30L", label: "30 e Lode" },
  { value: "idoneo", label: "Idoneo" },
  { value: "non_idoneo", label: "Non idoneo" },
];

export default function StoricoEsami() {
  const [academicYear, setAcademicYear] = useState("2025/2026");
  const [yearLevel, setYearLevel] = useState("1");
  const [courseId, setCourseId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [room, setRoom] = useState("");
  const [notes, setNotes] = useState("");
  const [teacherIds, setTeacherIds] = useState([]);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [gradeRows, setGradeRows] = useState({}); // { student_id: { grade_value, examiner_note } }

  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState(null); // { conflicts: [...] }

  const inputRefs = useRef({});

  // Carica corsi e docenti
  useEffect(() => {
    (async () => {
      try {
        const [c, t, sAll] = await Promise.all([
          api.get("/courses"),
          api.get("/teachers"),
          api.get("/students"),
        ]);
        setCourses(c.data || []);
        setTeachers(t.data || []);
        setAllStudents(sAll.data || []);
      } catch (e) {
        toast.error("Errore caricamento dati");
      }
    })();
  }, []);

  // Filtra corsi per anno
  const filteredCourses = useMemo(() => {
    return (courses || []).filter((c) => String(c.year_level) === String(yearLevel));
  }, [courses, yearLevel]);

  // Studenti disponibili per il picker (attivi + eventualmente ex)
  const availableStudents = useMemo(() => {
    if (includeInactive) return allStudents;
    return (allStudents || []).filter((s) => (s.status || "attivo") === "attivo");
  }, [allStudents, includeInactive]);

  // Studenti selezionati oggetti (per la tabella)
  const selectedStudents = useMemo(() => {
    const map = new Map((allStudents || []).map((s) => [s.id, s]));
    return selectedStudentIds
      .map((id) => map.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
  }, [allStudents, selectedStudentIds]);

  // Conteggio voti inseriti
  const filledCount = useMemo(() => {
    return selectedStudentIds.filter((id) => {
      const v = gradeRows[id]?.grade_value;
      return v && v !== "-" && v !== "—";
    }).length;
  }, [gradeRows, selectedStudentIds]);

  // Reset voti se cambio corso/data
  useEffect(() => {
    setGradeRows({});
    setPendingConflicts(null);
  }, [courseId, date]);

  const updateRow = (studentId, field, value) => {
    setGradeRows((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [field]: value },
    }));
  };

  const focusNext = (currentIdx) => {
    const nextStudent = selectedStudents[currentIdx + 1];
    if (nextStudent) {
      const ref = inputRefs.current[nextStudent.id];
      if (ref) ref.focus();
    }
  };

  const canSave = courseId && date && selectedStudentIds.length > 0 && filledCount > 0;

  const buildPayload = (overwrite = false) => {
    const entries = selectedStudentIds
      .map((sid) => {
        const row = gradeRows[sid];
        if (!row || !row.grade_value || row.grade_value === "-" || row.grade_value === "—") return null;
        return {
          student_id: sid,
          grade_value: row.grade_value,
          examiner_note: row.examiner_note || null,
        };
      })
      .filter(Boolean);
    return {
      course_id: courseId,
      date,
      year_academic: academicYear,
      time: time || null,
      room: room || null,
      notes: notes || null,
      examiner_teacher_ids: teacherIds.length ? teacherIds : null,
      entries,
      overwrite,
    };
  };

  const submit = async (overwrite = false) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = buildPayload(overwrite);
      const res = await api.post("/exams/bulk-grades", payload);
      const data = res.data;
      if (data.requires_confirmation) {
        setPendingConflicts({ conflicts: data.conflicts || [] });
        setSaving(false);
        return;
      }
      toast.success(`Salvato: ${data.created} nuovi, ${data.updated} aggiornati, ${data.skipped} saltati`);
      // Reset per il prossimo appello, mantenendo il corso e a.a.
      setGradeRows({});
      setPendingConflicts(null);
      setSelectedStudentIds([]);
      setDate("");
      setTime("");
      setRoom("");
      setNotes("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const confirmOverwrite = () => submit(true);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="storico-esami-page">
      <div className="flex items-center gap-3">
        <History className="w-6 h-6 text-emerald-700" />
        <div>
          <h1 className="text-2xl font-semibold">Storico Esami — Verbalizzazione massiva</h1>
          <p className="text-sm text-stone-500">Inserimento rapido dei voti degli anni accademici passati</p>
        </div>
      </div>

      {/* Barra selezione */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="w-4 h-4" /> Appello
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <Label>Anno accademico</Label>
              <Select value={academicYear} onValueChange={setAcademicYear}>
                <SelectTrigger data-testid="ay-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACADEMIC_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anno di corso</Label>
              <Select value={yearLevel} onValueChange={(v) => { setYearLevel(v); setCourseId(""); }}>
                <SelectTrigger data-testid="year-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1° anno</SelectItem>
                  <SelectItem value="2">2° anno</SelectItem>
                  <SelectItem value="3">3° anno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Corso</Label>
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger data-testid="course-select"><SelectValue placeholder={filteredCourses.length ? "Seleziona corso" : "Nessun corso per questo anno"} /></SelectTrigger>
                <SelectContent>
                  {filteredCourses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.cfu ? `(${c.cfu} CFU)` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data appello</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" />
            </div>
            <div>
              <Label>Ora (opz.)</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="time-input" />
            </div>
            <div className="md:col-span-3">
              <Label>Commissione (opz.)</Label>
              <TeacherMultiPicker
                teachers={teachers}
                values={teacherIds}
                onChange={setTeacherIds}
                testId="teachers-picker"
              />
            </div>
            <div className="md:col-span-3">
              <Label>Aula / Note (opz.)</Label>
              <Input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Aula o luogo" data-testid="room-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selezione studenti */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" /> Studenti ({selectedStudentIds.length})
          </CardTitle>
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="include-inactive" />
            Includi ex-studenti / archiviati
          </label>
        </CardHeader>
        <CardContent>
          <StudentPickerMulti
            students={availableStudents}
            values={selectedStudentIds}
            onChange={setSelectedStudentIds}
            testId="storico-students"
            placeholder="Cerca e aggiungi studenti che hanno sostenuto l'appello…"
          />
        </CardContent>
      </Card>

      {/* Tabella voti */}
      {selectedStudents.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Voti</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" data-testid="grade-counter">
                {filledCount} / {selectedStudentIds.length} voti inseriti
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b">
                  <tr>
                    <th className="py-2 pr-3 w-8">#</th>
                    <th className="py-2 pr-3">Studente</th>
                    <th className="py-2 pr-3 w-40">Voto</th>
                    <th className="py-2 pr-3">Note (opz.)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStudents.map((s, idx) => {
                    const row = gradeRows[s.id] || {};
                    return (
                      <tr key={s.id} className="border-b last:border-b-0" data-testid={`grade-row-${s.id}`}>
                        <td className="py-1.5 pr-3 text-stone-400">{idx + 1}</td>
                        <td className="py-1.5 pr-3">
                          <b>{s.last_name} {s.first_name}</b>
                          <span className="text-xs text-stone-500 ml-2">{s.matricola || ""} {(s.status && s.status !== "attivo") ? `· ${s.status}` : ""}</span>
                        </td>
                        <td className="py-1.5 pr-3">
                          <select
                            ref={(el) => { inputRefs.current[s.id] = el; }}
                            value={row.grade_value || "-"}
                            onChange={(e) => updateRow(s.id, "grade_value", e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                focusNext(idx);
                              }
                            }}
                            className="w-full border border-stone-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            data-testid={`grade-select-${s.id}`}
                          >
                            {GRADE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                        <td className="py-1.5 pr-3">
                          <Input
                            value={row.examiner_note || ""}
                            onChange={(e) => updateRow(s.id, "examiner_note", e.target.value)}
                            placeholder="Note facoltative"
                            className="h-8 text-sm"
                            data-testid={`note-input-${s.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setGradeRows({}); setPendingConflicts(null); }} data-testid="reset-grades-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Pulisci voti
              </Button>
              <Button onClick={() => submit(false)} disabled={!canSave || saving} data-testid="save-grades-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio…" : `Salva ${filledCount} voti`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modale conflitti */}
      {pendingConflicts && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="conflict-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-2 mb-3 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Voti già presenti</h2>
            </div>
            <p className="text-sm text-stone-600 mb-3">
              Per questa data e questo corso esistono già <b>{pendingConflicts.conflicts.length}</b> voti verbalizzati.
              Se confermi verranno <b>sovrascritti</b> con i nuovi valori.
            </p>
            <ul className="max-h-52 overflow-y-auto border rounded-md p-3 text-xs space-y-1 mb-4 bg-stone-50">
              {pendingConflicts.conflicts.map((c) => {
                const s = allStudents.find((x) => x.id === c.student_id);
                return (
                  <li key={c.student_id} className="flex justify-between">
                    <span>{s ? `${s.last_name} ${s.first_name}` : c.student_id}</span>
                    <span className="text-stone-500">voto attuale: <b>{c.grade_value}</b></span>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPendingConflicts(null)} data-testid="conflict-cancel-btn">Annulla</Button>
              <Button onClick={confirmOverwrite} disabled={saving} data-testid="conflict-confirm-btn" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Sovrascrittura…" : "Sovrascrivi"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Picker docenti multi con ricerca — evita i problemi del <Select> shadcn con selezioni multiple ripetute.
function TeacherMultiPicker({ teachers = [], values = [], onChange, testId = "teachers-picker" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedSet = new Set(values);
  const selectedList = useMemo(() => teachers.filter((t) => selectedSet.has(t.id)), [teachers, values]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = teachers.filter((t) => !selectedSet.has(t.id));
    if (!term) return base.slice(0, 40);
    return base.filter((t) => {
      const full = `${t.first_name || ""} ${t.last_name || ""} ${t.email || ""}`.toLowerCase();
      return full.includes(term);
    }).slice(0, 40);
  }, [teachers, q, values]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (id) => { onChange([...values, id]); setQ(""); };
  const remove = (id) => onChange(values.filter((v) => v !== id));

  return (
    <div ref={wrapRef} className="relative mt-1.5">
      <div className="border border-stone-200 rounded-md p-2 min-h-[42px] bg-white flex flex-wrap gap-1.5">
        {selectedList.map((t) => (
          <span key={t.id} data-testid={`${testId}-chip-${t.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-xs">
            {t.first_name} {t.last_name}
            <button type="button" onClick={() => remove(t.id)} className="text-stone-500 hover:text-red-600" aria-label="Rimuovi">
              <X size={11}/>
            </button>
          </span>
        ))}
        <div className="flex-1 relative min-w-[160px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400"/>
          <input
            data-testid={testId}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={selectedList.length === 0 ? "Aggiungi docenti commissione…" : "Aggiungi altro docente…"}
            className="w-full pl-6 pr-1 py-0.5 text-sm outline-none bg-transparent"
            autoComplete="off"
          />
        </div>
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`${testId}-opt-${t.id}`}
              onClick={() => add(t.id)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 flex items-center gap-2"
            >
              <span className="flex-1 truncate">{t.first_name} {t.last_name}</span>
              {t.email && <span className="text-[10px] text-stone-500 truncate max-w-[180px]">{t.email}</span>}
            </button>
          ))}
        </div>
      )}
      {open && q && filtered.length === 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg px-3 py-3 text-xs text-stone-400">
          Nessun docente trovato
        </div>
      )}
    </div>
  );
}
